/**
 * apiErrors — bilingual mapping of backend error details.
 *
 * Backend errors come in three shapes:
 *   1. { code, message }  — structured (preferred)
 *   2. plain string       — legacy Norwegian-only messages
 *   3. pydantic array     — [{ loc, msg, type }]
 *
 * `translateApiError(detail)` returns a display string in the CURRENT UI
 * language (read from localStorage, same key as LanguageContext), or null
 * when the detail is unusable (caller falls back to its own localized copy).
 */

const LANG_KEY = 'starcare_language';

export const currentLang = () => {
  try {
    return localStorage.getItem(LANG_KEY) === 'en' ? 'en' : 'no';
  } catch {
    return 'no';
  }
};

// code → English message (Norwegian path uses backend's own `message`)
const CODE_EN = {
  employer_sector_missing: "Set your organisation's sector before creating positions.",
  cross_sector_job_blocked: "The job's sector does not match your organisation's locked sector.",
  cannot_delete_non_draft: 'Only drafts can be deleted. Published positions must be archived instead.',
  sector_locked: 'Your sector is locked after the first choice. Contact admin to change it.',
  sector_already_set: 'Your sector is already chosen. Contact admin to change it.',
  sector_mismatch: 'This position belongs to a different sector than your profile.',
  professional_sector_missing: 'Choose your sector before applying to positions.',
  account_disabled: 'This account has been closed. Contact an administrator.',
  cannot_disable_self: 'You cannot close your own account.',
  already_disabled: 'The account is already closed.',
  not_disabled: 'The account is not closed.',
  invalid_name: 'The name must be at least 2 characters.',
  invalid_email: 'Invalid email address.',
  email_send_failed: 'Could not send the test email.',
};

// exact Norwegian backend strings → English
const EXACT_EN = {
  'Reset-lenken er utløpt.': 'The reset link has expired.',
  'Filen finnes ikke lenger på server.': 'The file no longer exists on the server.',
  'Når kategori er "annet" må custom_label være satt.': 'When the category is "other", a custom label is required.',
  'custom_label kan ikke være lengre enn 60 tegn.': 'The custom label cannot be longer than 60 characters.',
  'description kan ikke være lengre enn 280 tegn.': 'The description cannot be longer than 280 characters.',
  "custom_label er kun gyldig når kategori er 'annet'.": "A custom label is only valid when the category is 'other'.",
  'Kun kommuner kan be om dokumenttilgang.': 'Only employers can request document access.',
  'Sett organisasjonens sektor før du ber om tilgang.': "Set your organisation's sector before requesting access.",
  'Kandidaten tilhører en annen sektor.': 'The candidate belongs to a different sector.',
  'Ingen CV funnet.': 'No CV found.',
  'CV-filen finnes ikke lenger på server.': 'The CV file no longer exists on the server.',
  'Tittel er påkrevd.': 'A title is required.',
  'Ingen orgnr lagret — bruk POST /brreg-data først': 'No organisation number stored — fetch BRREG data first.',
  'Kun kommuner har lagrede kandidatfiltre.': 'Only employers have saved candidate filters.',
  'Kun kommuner kan bruke BRREG-oppslag': 'Only employers can use BRREG lookup.',
  'Kunne ikke lagre. Prøv igjen.': 'Could not save. Please try again.',
  'Kandidat ikke funnet': 'Candidate not found',
  'Organisasjon ikke funnet': 'Organisation not found',
};

// parametrized Norwegian strings → English (regex, $1 … capture groups)
const REGEX_EN = [
  [/^Kontoen er midlertidig sperret etter for mange feilede innloggingsforsøk\. Prøv igjen om (\d+) minutter\.$/,
    (m) => `The account is temporarily locked after too many failed login attempts. Try again in ${m[1]} minutes.`],
  [/^Du har nådd grensen på (\d+) opplastinger per døgn\. Prøv igjen senere\.$/,
    (m) => `You have reached the limit of ${m[1]} uploads per day. Please try again later.`],
  [/^Du har nådd grensen på (\d+) aktive dokumenter\. Slett et eldre dokument først\.$/,
    (m) => `You have reached the limit of ${m[1]} active documents. Delete an older document first.`],
  [/^Ugyldig kategori\./, () => 'Invalid document category.'],
  [/^orgnr_locked:/, () => 'The organisation number is locked — contact support to change it.'],
  [/^orgnr_required:/, () => 'An organisation number is required for employer accounts.'],
  [/^Sektoren \(([^)]+)\) matcher ikke organisasjonens sektor \(([^)]+)\)\.$/,
    (m) => `The sector (${m[1]}) does not match your organisation's sector (${m[2]}).`],
];

const translateStringEn = (s) => {
  if (EXACT_EN[s]) return EXACT_EN[s];
  for (const [rx, fn] of REGEX_EN) {
    const m = s.match(rx);
    if (m) return fn(m);
  }
  return null;
};

export const translateApiError = (detail) => {
  if (detail == null) return null;
  const en = currentLang() === 'en';

  if (Array.isArray(detail)) {
    // pydantic validation errors — msgs are already English
    const parts = detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : ''))
      .filter(Boolean);
    return parts.length ? parts.join(' · ') : null;
  }

  if (typeof detail === 'object') {
    if (en && detail.code && CODE_EN[detail.code]) return CODE_EN[detail.code];
    if (typeof detail.message === 'string') {
      return en ? (translateStringEn(detail.message) || detail.message) : detail.message;
    }
    if (typeof detail.msg === 'string') return detail.msg;
    return null;
  }

  if (typeof detail === 'string') {
    const m = detail.match(/^([a-z_]+):\s*(.+)$/);
    if (m && CODE_EN[m[1]]) return en ? CODE_EN[m[1]] : m[2];
    if (!en) return detail;
    return translateStringEn(detail) || detail;
  }

  return null;
};

// Convenience: translated detail, else a bilingual static fallback.
export const apiErrorText = (detail, fallbackNo, fallbackEn) => {
  const t = translateApiError(detail);
  if (t) return t;
  return currentLang() === 'en' ? (fallbackEn || fallbackNo) : fallbackNo;
};
